import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsFilePdf, BsTrash, BsDownload, BsArrowsMove } from 'react-icons/bs';
import { FiUploadCloud, FiFile } from 'react-icons/fi';
import { PDFDocument } from 'pdf-lib';
import SEO from '../components/SEO';
import { pdfMergeSchemas } from '../schemas';
import './ToolPage.css';

const PDFMerge = () => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    const filesWithPreview = pdfFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2)
    }));
    setFiles(prev => [...prev, ...filesWithPreview]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const removeFile = (id) => {
    setFiles(files.filter(f => f.id !== id));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverItem = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newFiles = [...files];
    const draggedFile = newFiles[draggedItem];
    newFiles.splice(draggedItem, 1);
    newFiles.splice(index, 0, draggedFile);
    
    setFiles(newFiles);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const mergePDFs = async () => {
    if (files.length < 2) {
      alert('Please select at least 2 PDF files to merge');
      return;
    }

    setIsProcessing(true);

    try {
      const mergedPdf = await PDFDocument.create();

      for (const fileData of files) {
        const arrayBuffer = await fileData.file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'merged-document.pdf';
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error merging PDFs:', error);
      alert('Error merging PDFs. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="tool-page">
      <SEO 
        title="Merge PDF Files Online – Combine PDFs Free"
        description="Combine multiple PDF files into one document online free. Fast, secure PDF merger — no watermark, no signup required."
        keywords="merge pdf, combine pdf files, pdf merger online"
        path="/pdf-merge"
        schema={pdfMergeSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFilePdf />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Merge PDF Files
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Combine multiple PDF documents into a single file. Drag to reorder.
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          {files.length === 0 ? (
            <motion.div
              className={`upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="upload-icon" />
              <h3>Drop PDF files here or click to browse</h3>
              <p>Select multiple PDF files to merge them</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </motion.div>
          ) : (
            <div className="pdf-editor-layout">
              <div className="pdf-files-panel">
                <div className="panel-header">
                  <h3>Files to Merge ({files.length})</h3>
                  <button 
                    className="btn-secondary-small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    + Add More
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="files-list">
                  <AnimatePresence>
                    {files.map((fileData, index) => (
                      <motion.div
                        key={fileData.id}
                        className={`file-item ${draggedItem === index ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOverItem(e, index)}
                        onDragEnd={handleDragEnd}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <BsArrowsMove className="drag-icon" />
                        <FiFile className="file-icon" />
                        <div className="file-info">
                          <div className="file-name">{fileData.name}</div>
                          <div className="file-size">{fileData.size} MB</div>
                        </div>
                        <button
                          className="btn-icon-danger"
                          onClick={() => removeFile(fileData.id)}
                          title="Remove"
                        >
                          <BsTrash />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              <div className="action-buttons">
                <motion.button
                  className="btn-primary-large"
                  onClick={mergePDFs}
                  disabled={isProcessing || files.length < 2}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <BsDownload />
                  {isProcessing ? 'Merging...' : `Merge ${files.length} PDFs`}
                </motion.button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFMerge;
